"use client";

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield,
  Users,
  UserCheck,
  Edit,
  Plus,
  Eye,
  Settings,
  Crown,
  Star,
  Activity,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabaseClient';

// Enhanced role and permission types
interface Permission {
  id: string;
  name: string;
  description: string;
  category: 'user_management' | 'content_management' | 'financial' | 'analytics' | 'system';
  level: 'read' | 'write' | 'admin';
}

interface Role {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  permissions: string[];
  userCount: number;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserWithRole {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: 'active' | 'suspended' | 'pending';
  lastActive: string;
  createdAt: string;
}

const UserRolesManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewRoleDialogOpen, setIsViewRoleDialogOpen] = useState(false);
  const [isEditRoleDialogOpen, setIsEditRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Mock data - in production, this would come from Supabase
  const mockPermissions: Permission[] = [
    // User Management
    { id: 'user_view', name: 'View Users', description: 'View user profiles and information', category: 'user_management', level: 'read' },
    { id: 'user_create', name: 'Create Users', description: 'Create new user accounts', category: 'user_management', level: 'write' },
    { id: 'user_edit', name: 'Edit Users', description: 'Modify user profiles and settings', category: 'user_management', level: 'write' },
    { id: 'user_delete', name: 'Delete Users', description: 'Delete user accounts', category: 'user_management', level: 'admin' },
    { id: 'role_manage', name: 'Manage Roles', description: 'Create and modify user roles', category: 'user_management', level: 'admin' },
    
    // Content Management
    { id: 'package_view', name: 'View Packages', description: 'View travel packages', category: 'content_management', level: 'read' },
    { id: 'package_create', name: 'Create Packages', description: 'Create new travel packages', category: 'content_management', level: 'write' },
    { id: 'package_approve', name: 'Approve Packages', description: 'Approve/reject package submissions', category: 'content_management', level: 'admin' },
    { id: 'agent_approve', name: 'Approve Agents', description: 'Approve/reject agent applications', category: 'content_management', level: 'admin' },
    
    // Financial
    { id: 'booking_view', name: 'View Bookings', description: 'View booking information', category: 'financial', level: 'read' },
    { id: 'payout_manage', name: 'Manage Payouts', description: 'Process agent payouts', category: 'financial', level: 'write' },
    { id: 'financial_reports', name: 'Financial Reports', description: 'Access financial reports and analytics', category: 'financial', level: 'admin' },
    
    // Analytics
    { id: 'analytics_view', name: 'View Analytics', description: 'Access basic analytics dashboards', category: 'analytics', level: 'read' },
    { id: 'analytics_advanced', name: 'Advanced Analytics', description: 'Access detailed analytics and insights', category: 'analytics', level: 'admin' },
    
    // System
    { id: 'system_settings', name: 'System Settings', description: 'Modify system configuration', category: 'system', level: 'admin' },
    { id: 'audit_logs', name: 'Audit Logs', description: 'View system audit logs', category: 'system', level: 'admin' },
  ];


  useEffect(() => {
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.warn('Loading timeout reached, setting loading to false');
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    fetchData();

    return () => clearTimeout(timeoutId);
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch only essential user data with limit for faster loading
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, role, created_at, updated_at')
        .order('created_at', { ascending: false })
        .limit(50); // Limit to 50 users for faster loading

      let transformedUsers: UserWithRole[] = [];

      if (usersError) {
        console.error('Error fetching users:', usersError);
        // Show error message instead of mock data
        setError('Failed to load users from database');
        setIsLoading(false);
        return;
      } else {
        // Transform users data to match our interface
        transformedUsers = (usersData || []).map((user: any) => ({
          id: user.id,
          name: user.name || 'Unknown',
          email: user.email,
          role: {
            id: user.role || 'customer',
            name: user.role === 'super_admin' ? 'Super Admin' : 
                  user.role === 'agent' ? 'Agent' : 'Customer',
            description: user.role === 'super_admin' ? 'Full system access' :
                        user.role === 'agent' ? 'Travel agent access' : 'Customer access',
            color: user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                   user.role === 'agent' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800',
            icon: user.role === 'super_admin' ? 'crown' : 
                  user.role === 'agent' ? 'user-check' : 'user',
            permissions: [],
            userCount: 1,
            isActive: true,
            isSystem: true,
            createdAt: user.created_at,
            updatedAt: user.updated_at
          },
          status: 'active' as const,
          lastActive: user.updated_at || user.created_at,
          createdAt: user.created_at
        }));

        // If no users from database, add mock data
        if (transformedUsers.length === 0) {
          transformedUsers = [
            {
              id: 'mock-user-1',
              name: 'John Doe',
              email: 'john@example.com',
              role: {
                id: 'customer',
                name: 'Customer',
                description: 'Regular customer with booking access',
                color: 'bg-gray-100 text-gray-800',
                icon: 'user',
                permissions: ['package_view', 'booking_create'],
                userCount: 1,
                isActive: true,
                isSystem: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              },
              status: 'active' as const,
              lastActive: new Date().toISOString(),
              createdAt: new Date().toISOString()
            },
            {
              id: 'mock-user-2',
              name: 'Jane Smith',
              email: 'jane@example.com',
              role: {
                id: 'agent',
                name: 'Agent',
                description: 'Travel agent with package and booking management',
                color: 'bg-blue-100 text-blue-800',
                icon: 'user-check',
                permissions: ['package_view', 'package_create', 'booking_view', 'booking_manage'],
                userCount: 1,
                isActive: true,
                isSystem: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              },
              status: 'active' as const,
              lastActive: new Date().toISOString(),
              createdAt: new Date().toISOString()
            }
          ];
        }
      }

      setUsers(transformedUsers);

      // Use static roles data for faster loading
      const staticRoles: Role[] = [
        {
          id: 'super_admin',
          name: 'Super Admin',
          description: 'Full system access with all permissions',
          color: 'bg-purple-100 text-purple-800',
          icon: 'crown',
          permissions: ['user_view', 'user_create', 'user_edit', 'user_delete', 'role_manage', 'package_approve', 'agent_approve'],
          userCount: transformedUsers.filter(u => u.role.id === 'super_admin').length,
          isActive: true,
          isSystem: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'agent',
          name: 'Agent',
          description: 'Travel agent with package and booking management',
          color: 'bg-blue-100 text-blue-800',
          icon: 'user-check',
          permissions: ['package_view', 'package_create', 'booking_view', 'booking_manage'],
          userCount: transformedUsers.filter(u => u.role.id === 'agent').length,
          isActive: true,
          isSystem: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'customer',
          name: 'Customer',
          description: 'Regular customer with booking access',
          color: 'bg-gray-100 text-gray-800',
          icon: 'user',
          permissions: ['package_view', 'booking_create'],
          userCount: transformedUsers.filter(u => u.role.id === 'customer').length,
          isActive: true,
          isSystem: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];

      setRoles(staticRoles);

      // Use static permissions data for faster loading
      const staticPermissions: Permission[] = [
        // User Management
        { id: 'user_view', name: 'View Users', description: 'View user profiles and information', category: 'user_management', level: 'read' },
        { id: 'user_create', name: 'Create Users', description: 'Create new user accounts', category: 'user_management', level: 'write' },
        { id: 'user_edit', name: 'Edit Users', description: 'Modify user profiles and settings', category: 'user_management', level: 'write' },
        { id: 'user_delete', name: 'Delete Users', description: 'Delete user accounts', category: 'user_management', level: 'admin' },
        { id: 'role_manage', name: 'Manage Roles', description: 'Create and modify user roles', category: 'user_management', level: 'admin' },
        
        // Content Management
        { id: 'package_view', name: 'View Packages', description: 'View travel packages', category: 'content_management', level: 'read' },
        { id: 'package_create', name: 'Create Packages', description: 'Create new travel packages', category: 'content_management', level: 'write' },
        { id: 'package_approve', name: 'Approve Packages', description: 'Approve/reject package submissions', category: 'content_management', level: 'admin' },
        { id: 'agent_approve', name: 'Approve Agents', description: 'Approve/reject agent applications', category: 'content_management', level: 'admin' },
        
        // Financial
        { id: 'booking_view', name: 'View Bookings', description: 'View booking information', category: 'financial', level: 'read' },
        { id: 'booking_create', name: 'Create Bookings', description: 'Create new bookings', category: 'financial', level: 'write' },
        { id: 'booking_manage', name: 'Manage Bookings', description: 'Manage all bookings', category: 'financial', level: 'admin' },
        { id: 'payout_view', name: 'View Payouts', description: 'View payout information', category: 'financial', level: 'read' },
        { id: 'payout_process', name: 'Process Payouts', description: 'Process agent payouts', category: 'financial', level: 'admin' },
        
        // Analytics
        { id: 'analytics_view', name: 'View Analytics', description: 'View system analytics', category: 'analytics', level: 'read' },
        { id: 'analytics_export', name: 'Export Analytics', description: 'Export analytics data', category: 'analytics', level: 'write' },
        
        // System
        { id: 'system_settings', name: 'System Settings', description: 'Manage system settings', category: 'system', level: 'admin' },
        { id: 'system_logs', name: 'System Logs', description: 'View system logs', category: 'system', level: 'admin' }
      ];

      setPermissions(staticPermissions);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch user and role data. Using cached data.',
      });
      
      // Set mock data to prevent blank screen
      const mockUsers: UserWithRole[] = [
        {
          id: 'mock-user-1',
          name: 'John Doe',
          email: 'john@example.com',
          role: {
            id: 'customer',
            name: 'Customer',
            description: 'Regular customer with booking access',
            color: 'bg-gray-100 text-gray-800',
            icon: 'user',
            permissions: ['package_view', 'booking_create'],
            userCount: 1,
            isActive: true,
            isSystem: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          status: 'active' as const,
          lastActive: new Date().toISOString(),
          createdAt: new Date().toISOString()
        },
        {
          id: 'mock-user-2',
          name: 'Jane Smith',
          email: 'jane@example.com',
          role: {
            id: 'agent',
            name: 'Agent',
            description: 'Travel agent with package and booking management',
            color: 'bg-blue-100 text-blue-800',
            icon: 'user-check',
            permissions: ['package_view', 'package_create', 'booking_view', 'booking_manage'],
            userCount: 1,
            isActive: true,
            isSystem: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          status: 'active' as const,
          lastActive: new Date().toISOString(),
          createdAt: new Date().toISOString()
        },
        {
          id: 'mock-user-3',
          name: 'Admin User',
          email: 'admin@example.com',
          role: {
            id: 'super_admin',
            name: 'Super Admin',
            description: 'Full system access with all permissions',
            color: 'bg-purple-100 text-purple-800',
            icon: 'crown',
            permissions: ['user_view', 'user_create', 'user_edit', 'user_delete', 'role_manage', 'package_approve', 'agent_approve'],
            userCount: 1,
            isActive: true,
            isSystem: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          status: 'active' as const,
          lastActive: new Date().toISOString(),
          createdAt: new Date().toISOString()
        }
      ];

      const mockRoles: Role[] = [
        {
          id: 'super_admin',
          name: 'Super Admin',
          description: 'Full system access with all permissions',
          color: 'bg-purple-100 text-purple-800',
          icon: 'crown',
          permissions: ['user_view', 'user_create', 'user_edit', 'user_delete', 'role_manage', 'package_approve', 'agent_approve'],
          userCount: 1,
          isActive: true,
          isSystem: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'agent',
          name: 'Agent',
          description: 'Travel agent with package and booking management',
          color: 'bg-blue-100 text-blue-800',
          icon: 'user-check',
          permissions: ['package_view', 'package_create', 'booking_view', 'booking_manage'],
          userCount: 1,
          isActive: true,
          isSystem: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'customer',
          name: 'Customer',
          description: 'Regular customer with booking access',
          color: 'bg-gray-100 text-gray-800',
          icon: 'user',
          permissions: ['package_view', 'booking_create'],
          userCount: 1,
          isActive: true,
          isSystem: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];

      const mockPermissions: Permission[] = [
        // User Management
        { id: 'user_view', name: 'View Users', description: 'View user profiles and information', category: 'user_management', level: 'read' },
        { id: 'user_create', name: 'Create Users', description: 'Create new user accounts', category: 'user_management', level: 'write' },
        { id: 'user_edit', name: 'Edit Users', description: 'Modify user profiles and settings', category: 'user_management', level: 'write' },
        { id: 'user_delete', name: 'Delete Users', description: 'Delete user accounts', category: 'user_management', level: 'admin' },
        { id: 'role_manage', name: 'Manage Roles', description: 'Create and modify user roles', category: 'user_management', level: 'admin' },
        
        // Content Management
        { id: 'package_view', name: 'View Packages', description: 'View travel packages', category: 'content_management', level: 'read' },
        { id: 'package_create', name: 'Create Packages', description: 'Create new travel packages', category: 'content_management', level: 'write' },
        { id: 'package_approve', name: 'Approve Packages', description: 'Approve/reject package submissions', category: 'content_management', level: 'admin' },
        { id: 'agent_approve', name: 'Approve Agents', description: 'Approve/reject agent applications', category: 'content_management', level: 'admin' },
        
        // Financial
        { id: 'booking_view', name: 'View Bookings', description: 'View booking information', category: 'financial', level: 'read' },
        { id: 'booking_create', name: 'Create Bookings', description: 'Create new bookings', category: 'financial', level: 'write' },
        { id: 'booking_manage', name: 'Manage Bookings', description: 'Manage all bookings', category: 'financial', level: 'admin' },
        { id: 'payout_view', name: 'View Payouts', description: 'View payout information', category: 'financial', level: 'read' },
        { id: 'payout_process', name: 'Process Payouts', description: 'Process agent payouts', category: 'financial', level: 'admin' },
        
        // Analytics
        { id: 'analytics_view', name: 'View Analytics', description: 'View system analytics', category: 'analytics', level: 'read' },
        { id: 'analytics_export', name: 'Export Analytics', description: 'Export analytics data', category: 'analytics', level: 'write' },
        
        // System
        { id: 'system_settings', name: 'System Settings', description: 'Manage system settings', category: 'system', level: 'admin' },
        { id: 'system_logs', name: 'System Logs', description: 'View system logs', category: 'system', level: 'admin' }
      ];

      setUsers(mockUsers);
      setRoles(mockRoles);
      setPermissions(mockPermissions);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRole = async (roleData: Partial<Role>) => {
    try {
      // In production, save to Supabase
      const newRole: Role = {
        id: `role_${Date.now()}`,
        name: roleData.name || '',
        description: roleData.description || '',
        color: roleData.color || 'bg-gray-100 text-gray-800',
        icon: roleData.icon || 'Users',
        permissions: roleData.permissions || [],
        userCount: 0,
        isActive: true,
        isSystem: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setRoles(prev => [...prev, newRole]);
      setIsRoleDialogOpen(false);
      
      toast({
        title: 'Role Created',
        description: `Role "${newRole.name}" has been created successfully`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create role',
      });
    }
  };

  const handleUpdateUserRole = async (userId: string, newRoleId: string) => {
    try {
      const newRole = roles.find(r => r.id === newRoleId);
      if (!newRole) return;

      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));

      toast({
        title: 'Role Updated',
        description: 'User role has been updated successfully',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update user role',
      });
    }
  };

  const handleViewRole = (role: Role) => {
    setSelectedRole(role);
    setIsViewRoleDialogOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setIsEditRoleDialogOpen(true);
  };

  const handleUpdateRole = async (roleData: Partial<Role>) => {
    if (!editingRole) return;

    try {
      // In a real implementation, you'd update the role in Supabase
      // For now, we'll update the local state
      setRoles(prevRoles => 
        prevRoles.map(role => 
          role.id === editingRole.id 
            ? { ...role, ...roleData, updatedAt: new Date().toISOString() }
            : role
        )
      );

      toast({
        title: 'Success',
        description: 'Role updated successfully',
      });

      setIsEditRoleDialogOpen(false);
      setEditingRole(null);
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update role',
      });
    }
  };

  const getPermissionsByCategory = (category: string) => {
    return permissions.filter(p => p.category === category);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'user_management': return Users;
      case 'content_management': return UserCheck;
      case 'financial': return Star;
      case 'analytics': return Activity;
      case 'system': return Settings;
      default: return Shield;
    }
  };

  const getRoleIcon = (iconName: string) => {
    switch (iconName) {
      case 'Crown': return Crown;
      case 'Shield': return Shield;
      case 'UserCheck': return UserCheck;
      case 'Star': return Star;
      case 'Users': return Users;
      default: return Shield;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-gray-600">Loading user roles and permissions...</span>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Roles & Permissions</h2>
          <p className="text-gray-600">Manage user roles and access permissions</p>
          {users.length > 0 && !error && (
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="bg-green-100 text-green-800">
                ✅ Live Database Data
              </Badge>
              <span className="text-xs text-gray-500">
                Real-time user data from Supabase
              </span>
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 mt-1">
              ❌ {error}
            </p>
          )}
        </div>
        <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Role</DialogTitle>
              <DialogDescription>
                Define a new role with specific permissions
              </DialogDescription>
            </DialogHeader>
            <RoleFormContent 
              permissions={permissions}
              onSubmit={handleCreateRole}
              onCancel={() => setIsRoleDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="roles" className="space-y-6">
        <TabsList>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => {
              const IconComponent = getRoleIcon(role.icon);
              return (
                <Card key={role.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <IconComponent className="h-5 w-5" />
                        <CardTitle className="text-lg">{role.name}</CardTitle>
                      </div>
                      <Badge className={role.color}>{role.userCount} users</Badge>
                    </div>
                    <CardDescription>{role.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Permissions ({role.permissions.length})</p>
                        <div className="flex flex-wrap gap-1">
                          {role.permissions.slice(0, 3).map((permId) => {
                            const perm = permissions.find(p => p.id === permId);
                            return perm ? (
                              <Badge key={permId} variant="outline" className="text-xs">
                                {perm.name}
                              </Badge>
                            ) : null;
                          })}
                          {role.permissions.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{role.permissions.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {role.isActive ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          )}
                          <span className="text-sm text-gray-600">
                            {role.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {role.isSystem && (
                          <Badge variant="secondary" className="text-xs">System</Badge>
                        )}
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleViewRole(role)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                        {!role.isSystem && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleEditRole(role)}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>All Users</CardTitle>
              <CardDescription>Manage user accounts and role assignments</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const IconComponent = getRoleIcon(user.role.icon);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-600">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <IconComponent className="h-4 w-4" />
                            <Badge className={user.role.color}>{user.role.name}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600">
                            {new Date(user.lastActive).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Select
                              value={user.role.id}
                              onValueChange={(newRoleId) => handleUpdateUserRole(user.id, newRoleId)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.map((role) => (
                                  <SelectItem key={role.id} value={role.id}>
                                    {role.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-6">
          <div className="space-y-6">
            {['user_management', 'content_management', 'financial', 'analytics', 'system'].map((category) => {
              const IconComponent = getCategoryIcon(category);
              const categoryPermissions = getPermissionsByCategory(category);
              
              return (
                <Card key={category}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-5 w-5" />
                      <CardTitle className="capitalize">
                        {category.replace('_', ' ')} Permissions
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoryPermissions.map((permission) => (
                        <div key={permission.id} className="p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{permission.name}</h4>
                            <Badge 
                              variant={permission.level === 'admin' ? 'destructive' : 
                                       permission.level === 'write' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {permission.level}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{permission.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* View Role Dialog */}
      <Dialog open={isViewRoleDialogOpen} onOpenChange={setIsViewRoleDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Role Details</DialogTitle>
            <DialogDescription>
              View detailed information about this role and its permissions
            </DialogDescription>
          </DialogHeader>
          {selectedRole && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Role Name</Label>
                  <p className="text-sm text-gray-600">{selectedRole.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <Badge className={selectedRole.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {selectedRole.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium">Description</Label>
                  <p className="text-sm text-gray-600">{selectedRole.description}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Users with this role</Label>
                  <p className="text-sm text-gray-600">{selectedRole.userCount} users</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Created</Label>
                  <p className="text-sm text-gray-600">
                    {new Date(selectedRole.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Permissions ({selectedRole.permissions.length})</Label>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {selectedRole.permissions.map((permId) => {
                    const perm = permissions.find(p => p.id === permId);
                    return perm ? (
                      <div key={permId} className="p-2 border border-gray-200 rounded">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{perm.name}</span>
                          <Badge 
                            variant={perm.level === 'admin' ? 'destructive' : 
                                     perm.level === 'write' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {perm.level}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600">{perm.description}</p>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewRoleDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditRoleDialogOpen} onOpenChange={setIsEditRoleDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>
              Modify role details and permissions
            </DialogDescription>
          </DialogHeader>
          {editingRole && (
            <RoleFormContent 
              permissions={permissions}
              onSubmit={handleUpdateRole}
              onCancel={() => {
                setIsEditRoleDialogOpen(false);
                setEditingRole(null);
              }}
              initialData={editingRole}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Role form component
const RoleFormContent: React.FC<{
  permissions: Permission[];
  onSubmit: (data: Partial<Role>) => void;
  onCancel: () => void;
  initialData?: Partial<Role>; // Added initialData prop
}> = ({ permissions, onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    color: initialData?.color || 'bg-blue-100 text-blue-800',
    icon: initialData?.icon || 'Users',
    permissions: initialData?.permissions || [] as string[],
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || 'bg-blue-100 text-blue-800',
        icon: initialData.icon || 'Users',
        permissions: initialData.permissions || [],
      }));
    }
  }, [initialData]);

  const handlePermissionToggle = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Role Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter role name"
            required
          />
        </div>
        <div>
          <Label htmlFor="color">Color</Label>
          <Select value={formData.color} onValueChange={(value) => setFormData(prev => ({ ...prev, color: value }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bg-blue-100 text-blue-800">Blue</SelectItem>
              <SelectItem value="bg-green-100 text-green-800">Green</SelectItem>
              <SelectItem value="bg-purple-100 text-purple-800">Purple</SelectItem>
              <SelectItem value="bg-red-100 text-red-800">Red</SelectItem>
              <SelectItem value="bg-yellow-100 text-yellow-800">Yellow</SelectItem>
              <SelectItem value="bg-gray-100 text-gray-800">Gray</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Enter role description"
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="icon">Icon</Label>
        <Select value={formData.icon} onValueChange={(value) => setFormData(prev => ({ ...prev, icon: value }))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Users">Users</SelectItem>
            <SelectItem value="UserCheck">User Check</SelectItem>
            <SelectItem value="Crown">Crown</SelectItem>
            <SelectItem value="Shield">Shield</SelectItem>
            <SelectItem value="Settings">Settings</SelectItem>
            <SelectItem value="Activity">Activity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Permissions</Label>
        <div className="mt-2 space-y-4">
          {['user_management', 'content_management', 'financial', 'analytics', 'system'].map((category) => {
            const categoryPermissions = permissions.filter(p => p.category === category);
            return (
              <div key={category} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium mb-3 capitalize">{category.replace('_', ' ')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {categoryPermissions.map((permission) => (
                    <div key={permission.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={permission.id}
                        checked={formData.permissions.includes(permission.id)}
                        onCheckedChange={() => handlePermissionToggle(permission.id)}
                      />
                      <Label htmlFor={permission.id} className="text-sm">
                        {permission.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {initialData ? 'Update Role' : 'Create Role'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default UserRolesManagement;
